import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ActionSelector from './ActionSelector';
import IconPicker from './IconPicker';
import ColorPicker from './ColorPicker';
import UploadedImageField from './UploadedImageField';
import {
  ICON_TYPE_BUILTIN,
  ICON_TYPE_IMAGE,
  ICON_TYPE_TEXT,
  DEFAULT_ICON_BG,
} from './constants';
import { deriveInitials, buildInitialEditorValues } from './helpers';
import { EDITOR_DIALOG_Z_INDEX, EDITOR_MENU_PROPS } from './editorLayers';

/**
 * Shared button editor dialog.
 *
 * Owns form state for label / action / icon visuals.
 * Action-specific config is supplied via `actions[].renderConfig`.
 * Persistence and API loading stay in the caller/adapter.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.button - source button used to reset form when opened/changed
 * @param {string} [props.title]
 * @param {Array<{
 *   value: string,
 *   label: string,
 *   disabled?: boolean,
 *   renderConfig?: (ctx: {
 *     values: object,
 *     setValues: (patch: object|((prev: object)=>object)) => void,
 *     button: object|null,
 *   }) => import('react').ReactNode,
 * }>} props.actions
 * @param {(values: object) => boolean} props.canSave
 * @param {(values: object) => object} props.buildSavePayload
 * @param {boolean} [props.actionSelectDisabled]
 * @param {boolean} [props.canDelete]
 * @param {object} [props.valuePatches] - shallow merge into form values without full reset
 *   (used by HomeLauncher for UUID→canonical flowId remap)
 * @param {(ctx: { values: object }) => import('react').ReactNode} [props.renderExtraContent]
 * @param {() => void} props.onClose
 * @param {(payload: object) => void} props.onSave
 * @param {() => void} [props.onDelete]
 * @param {{ tenantId: string, scope: 'personal'|'shared', upload: (file: File) => Promise<string> }|null} [props.customImage]
 */
export default function ButtonEditor({
  open,
  button = null,
  title = 'Edit Button',
  actions = [],
  canSave,
  buildSavePayload,
  actionSelectDisabled = false,
  canDelete = false,
  valuePatches = null,
  renderExtraContent = null,
  customImage = null,
  onClose,
  onSave,
  onDelete,
}) {
  const [values, setValuesState] = useState(() => buildInitialEditorValues(button));
  const [iconSearch, setIconSearch] = useState('');
  const [imageBusy, setImageBusy] = useState(false);

  const setValues = (patch) => {
    setValuesState((prev) =>
      typeof patch === 'function' ? patch(prev) : { ...prev, ...patch }
    );
  };

  useEffect(() => {
    setValuesState(buildInitialEditorValues(button));
    setIconSearch('');
    setImageBusy(false);
  }, [button]);

  useEffect(() => {
    if (!valuePatches || typeof valuePatches !== 'object') return;
    setValuesState((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(valuePatches).forEach((key) => {
        if (valuePatches[key] !== prev[key]) {
          next[key] = valuePatches[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [valuePatches]);

  const saveEnabled = useMemo(() => {
    if (imageBusy) return false;
    if (typeof canSave === 'function') {
      return canSave({ ...values, iconUploadBusy: imageBusy });
    }
    return !!(values.label || '').trim();
  }, [canSave, imageBusy, values]);

  const activeAction = actions.find((a) => a.value === values.actionType);

  const handleIconTypeChange = (next) => {
    setValues((prev) => {
      const patch = { iconType: next };
      if (next === ICON_TYPE_TEXT && !prev.iconText) {
        patch.iconText = deriveInitials(prev.label || button?.label || '');
      }
      if (next === ICON_TYPE_TEXT && !prev.iconBg) {
        patch.iconBg = DEFAULT_ICON_BG;
      }
      return { ...prev, ...patch };
    });
  };

  const handleSave = () => {
    if (!onSave || !buildSavePayload) return;
    onSave(buildSavePayload(values));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" sx={{ zIndex: EDITOR_DIALOG_Z_INDEX }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            label="Button Name"
            fullWidth
            value={values.label}
            onChange={(e) => setValues({ label: e.target.value })}
          />

          <ActionSelector
            value={values.actionType}
            onChange={(actionType) => setValues({ actionType })}
            actions={actions}
            disabled={actionSelectDisabled}
          />

          {typeof activeAction?.renderConfig === 'function'
            ? activeAction.renderConfig({
                values,
                setValues,
                button,
              })
            : null}

          <FormControl fullWidth>
            <InputLabel id="icon-type-label">Icon Type</InputLabel>
            <Select
              labelId="icon-type-label"
              label="Icon Type"
              value={values.iconType || ICON_TYPE_BUILTIN}
              onChange={(e) => handleIconTypeChange(e.target.value)}
              MenuProps={EDITOR_MENU_PROPS}
            >
              <MenuItem value={ICON_TYPE_BUILTIN}>Built-in icon</MenuItem>
              <MenuItem value={ICON_TYPE_TEXT}>Color + text</MenuItem>
              {customImage ? <MenuItem value={ICON_TYPE_IMAGE}>Uploaded image</MenuItem> : null}
            </Select>
          </FormControl>

          {values.iconType === ICON_TYPE_BUILTIN && (
            <IconPicker
              value={values.iconKey || ''}
              onChange={(iconKey) => setValues({ iconKey })}
              search={iconSearch}
              onSearchChange={setIconSearch}
            />
          )}

          {customImage && values.iconType === ICON_TYPE_IMAGE && (
            <UploadedImageField
              assetId={values.iconImageAssetId}
              tenantId={customImage.tenantId}
              alt={values.label || 'Button image'}
              helperText="PNG, JPEG, or WebP. The saved button stores the image id only."
              onUpload={customImage.upload}
              onUploaded={assetId => setValues({ iconImageAssetId: assetId })}
              onBusyChange={setImageBusy}
            />
          )}

          {values.iconType === ICON_TYPE_TEXT && (
            <Stack spacing={1}>
              <TextField
                label="Icon Text"
                helperText="Up to 5 characters"
                value={values.iconText || ''}
                onChange={(e) =>
                  setValues({
                    iconText: e.target.value.toUpperCase().slice(0, 5),
                  })
                }
                inputProps={{ maxLength: 5 }}
              />
              <ColorPicker
                value={values.iconBg || DEFAULT_ICON_BG}
                onChange={(iconBg) => setValues({ iconBg })}
              />
              <Typography variant="caption" color="text.secondary">
                The text will be shown large inside the button tile.
              </Typography>
            </Stack>
          )}
          {typeof renderExtraContent === 'function' ? renderExtraContent({ values }) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {canDelete && (
          <Button color="error" onClick={onDelete}>
            Delete
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!saveEnabled}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
